import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { TEAM_OPTIONS, teamThemes, type TeamKey } from '../theme/teamPalette';
import { Avatar } from '../theme/icons';

type Invite = {
  code: string;
  createdAt: string;
  usedAt: string | null;
  usedBy: { username: string; displayName: string } | null;
};

export default function MePage() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [favoriteTeam, setFavoriteTeam] = useState(user?.favoriteTeam ?? '');
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: () => api<{ codes: Invite[] }>('/api/invites').then((r) => r.codes),
    staleTime: 10_000,
  });

  const createInvite = useMutation({
    mutationFn: () => api<{ code: string }>('/api/invites', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });

  const updateMe = useMutation({
    mutationFn: (data: { favoriteTeam: string | null }) =>
      api<unknown>('/api/auth/me', { method: 'PATCH', body: data }),
    onSuccess: () => {
      void refreshMe();
      setSavedMsg('Gespeichert');
      setTimeout(() => setSavedMsg(null), 1500);
    },
  });

  if (!user) return <Navigate to="/login" replace />;

  async function copy(code: string) {
    try { await navigator.clipboard.writeText(code); } catch { /* no-op */ }
  }

  return (
    <div className="min-h-full safe-top safe-bottom px-4 pb-10 relative z-[1]">
      <header className="flex items-center gap-3 pt-3 pb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-[34px] h-[34px] rounded-[11px] bg-surface2 flex items-center justify-center text-text"
          aria-label="Zurück"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <h1 className="font-sans text-[22px] font-extrabold text-text">Profil</h1>
      </header>

      <section className="bg-surface border border-border rounded-[18px] p-5 flex items-center gap-4 mb-6">
        <Avatar name={user.displayName} size={56} team={Boolean(user.favoriteTeam)} />
        <div>
          <p className="font-sans text-[18px] font-extrabold text-text leading-tight">
            {user.displayName}
          </p>
          <p className="font-mono text-[11px] text-muted mt-0.5">@{user.username}</p>
        </div>
      </section>

      <SectionLabel>LIEBLINGSTEAM</SectionLabel>
      <div className="grid grid-cols-2 gap-2 mt-2 mb-3">
        <TeamChip
          code=""
          label="Kein Team"
          primary="var(--color-surface-2)"
          accent="var(--color-muted)"
          active={favoriteTeam === ''}
          onSelect={setFavoriteTeam}
        />
        {TEAM_OPTIONS.map((t) => {
          const palette = teamThemes[t.code as TeamKey] ?? teamThemes.default;
          return (
            <TeamChip
              key={t.code}
              code={t.code}
              label={t.label}
              primary={palette.primary}
              accent={palette.accent}
              active={favoriteTeam === t.code}
              onSelect={setFavoriteTeam}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-3 mb-8">
        <button
          type="button"
          onClick={() => updateMe.mutate({ favoriteTeam: favoriteTeam || null })}
          disabled={updateMe.isPending || favoriteTeam === (user.favoriteTeam ?? '')}
          className="px-4 py-2.5 rounded-[12px] bg-primary text-white font-sans font-bold text-[13px] disabled:opacity-50"
        >
          Speichern
        </button>
        {savedMsg && (
          <span className="font-sans text-[13px] font-semibold" style={{ color: 'var(--status-have)' }}>
            {savedMsg}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <SectionLabel>EINLADUNGSCODES</SectionLabel>
        <button
          type="button"
          onClick={() => createInvite.mutate()}
          disabled={createInvite.isPending}
          className="px-3 py-1.5 rounded-[10px] bg-accent text-bg font-sans font-bold text-[12px] disabled:opacity-50"
        >
          + Neu
        </button>
      </div>

      {invites.data?.length === 0 && (
        <p className="font-sans text-sm text-muted mb-6">
          Noch keine. Drück „+ Neu" für deinen ersten Code.
        </p>
      )}

      <ul className="space-y-2 mb-10">
        {invites.data?.map((c) => (
          <li
            key={c.code}
            className="bg-surface border border-border rounded-[14px] p-3.5 flex items-center justify-between gap-3"
          >
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[14px] text-text">{c.code}</p>
              <p className="font-mono text-[10px] text-muted mt-0.5">
                {c.usedAt
                  ? `eingelöst von ${c.usedBy?.displayName ?? c.usedBy?.username}`
                  : 'frei'}
              </p>
            </div>
            {!c.usedAt && (
              <button
                type="button"
                onClick={() => copy(c.code)}
                className="px-3 py-1.5 rounded-[10px] bg-surface2 text-text font-sans font-bold text-[12px]"
              >
                Kopieren
              </button>
            )}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={logout}
        className="w-full py-3 rounded-[14px] bg-surface border border-border text-muted font-sans text-[13px]"
      >
        Abmelden
      </button>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-mono text-[10px] font-bold tracking-[.12em] text-muted">{children}</p>
  );
}

function TeamChip({
  code,
  label,
  primary,
  accent,
  active,
  onSelect,
}: {
  code: string;
  label: string;
  primary: string;
  accent: string;
  active: boolean;
  onSelect: (v: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(code)}
      className={`flex items-center gap-3 p-3 rounded-[14px] bg-surface border transition-colors text-left ${
        active ? 'border-primary' : 'border-border'
      }`}
    >
      <span className="flex flex-col flex-none">
        <span className="w-7 h-3 rounded-t-[6px]" style={{ background: primary }} />
        <span className="w-7 h-3 rounded-b-[6px]" style={{ background: accent }} />
      </span>
      <span className="flex-1 font-sans font-bold text-[13px] text-text">{label}</span>
      {active && (
        <span className="font-mono text-[10px] tracking-[.08em] text-primary">✓</span>
      )}
    </button>
  );
}
